import cn from 'classnames'
import styles from 'components/features/pfm/GenericFloatingPane/GenericFloatingPane.styl'
import { FC, useMemo, useCallback, useContext, useRef } from 'react'
import { FloatingPaneHeader, FloatingPaneHeaderProps } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { FloatingPaneFooter, FloatingPaneFooterProps } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import { FloatingPaneLayout, FloatingPaneLayoutProps } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { killAllFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import Button from 'components/features/pfm/Button/Button'
import { BACK_BUTTON_PROPS, CANCEL_BUTTON_PROPS, CardMenuPaneButton, KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { PaneContext } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'

type CreditCardPanePresetProps =
  & Omit<FloatingPaneLayoutProps, 'type'>
  & FloatingPaneHeaderProps
  & FloatingPaneFooterProps
  & {
    onFooterCancel?: (() => void) | null
    noPadding?: boolean
  }

export const CreditCardPanePreset: FC<CreditCardPanePresetProps> = (props) => {
  const {
    cancelTheme,
    multiCardLayout,
    onClose,
    primaryButton,
    secondaryButton,
    tertiaryButton,
    onFooterCancel,
    progressBarPercent,
    isLoading,
    children,
    notification,
    title,
    subTitle,
    superTitle,
    iconSlug,
    bodyClassName,
    wrapperClassName,
    verticallySpreadLayout = true,
    noPadding,
    withStaticFooter = true,
  } = props

  const dispatch = useStoreDispatch()
  const pane = useContext(PaneContext)
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const BackButton = useMemo(() => {
    if (secondaryButton === null || pane?.backButton === null) {
      return null

    } else if (!!secondaryButton) {
      // If passed directly from props.
      return secondaryButton

      // To chain backButtons, pass them around in modifyFloatingPane
      // action calls and access them here
    } else if (!!pane?.backButton) {
      return pane.backButton

    }
    // Default Case. Pass a ref to be able to trigger this button on swipe
    return <CardMenuPaneButton { ...BACK_BUTTON_PROPS } innerRef={ backButtonRef } />

  }, [pane, secondaryButton]) as typeof Button

  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const CancelButton = useMemo(() => {
    if (tertiaryButton || tertiaryButton === null) {
      return tertiaryButton
    }
    return <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } innerRef={ cancelButtonRef } />
  }, [tertiaryButton]) as typeof Button

  const handleCancel = useCallback(() => (
    !!onClose ? onClose() : dispatch(killAllFloatingPanesActionCreator())
  ), [dispatch, onClose])

  const getCancelTheme = useMemo(() => {
    if (cancelTheme) {
      return cancelTheme
    } else if (!!BackButton) {
      return undefined
    }
    return 'blue'
  }, [cancelTheme, BackButton])

  const handleOnSwipeRight = useCallback(() => {
    // @ts-expect-error Props are still accessible
    const secondaryRef = BackButton?.props?.innerRef
    // @ts-expect-error Props are still accessible
    const tertiaryRef = CancelButton?.props?.innerRef
    const buttonToClick = secondaryRef?.current || tertiaryRef?.current

    return !!buttonToClick && buttonToClick?.click()
  }, [BackButton, CancelButton])

  const Header = useMemo(() => (
    <FloatingPaneHeader
      isLoading={ isLoading }
      superTitle={ superTitle || 'YOUR CARD' }
      title={ title }
      subTitle={ subTitle }
      iconSlug={ iconSlug }
      progressBarPercent={ progressBarPercent }
      onClose={ handleCancel }
      theme='cyan'
      compact
    />
  ), [isLoading, superTitle, title, subTitle, iconSlug, progressBarPercent, handleCancel])

  const Footer = useMemo(() => (
    <FloatingPaneFooter
      isLoading={ isLoading }
      primaryButton={ primaryButton }
      secondaryButton={ BackButton }
      tertiaryButton={ CancelButton }
      onCancel={ onFooterCancel !== undefined ? onFooterCancel : handleCancel }
      cancelTheme={ getCancelTheme }
    />
  ), [isLoading, primaryButton, BackButton, CancelButton, onFooterCancel, handleCancel, getCancelTheme])

  return (
    <FloatingPaneLayout
      type='advisor'
      header={ Header }
      notification={ notification }
      children={ children }
      footer={ Footer }
      withStaticFooter={ withStaticFooter }
      verticallySpreadLayout={ verticallySpreadLayout }
      bodyClassName={ cn(bodyClassName, styles.panePreset, { [styles.noPadding]: !!noPadding }) }
      wrapperClassName={ wrapperClassName }
      onSwipeRight={ handleOnSwipeRight }
      multiCardLayout={ multiCardLayout }
    />
  )
}
